import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import type { ImportValidationReport } from '@/services/organizationImportService';

interface StepConfirmProps {
  report: ImportValidationReport;
  fileName: string;
}

export const StepConfirm: React.FC<StepConfirmProps> = ({ report, fileName }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold mb-1">
          {t('organizations.import.confirm.title', 'Confirmation')}
        </h4>
        <p className="text-sm text-muted-foreground">
          {t(
            'organizations.import.confirm.description',
            "Vérifiez les éléments à créer puis lancez l'import. L'opération est atomique : si une étape échoue, l'organisation et tout ce qui a été créé sera annulé.",
          )}
        </p>
      </div>

      <dl className="rounded-md border border-border divide-y divide-border text-sm">
        <Row label={t('organizations.import.confirm.fileLabel', 'Fichier')} value={fileName} />
        <Row
          label={t('organizations.import.confirm.organizationLabel', 'Organisation')}
          value={report.organization_name || '—'}
        />
        <Row
          label={t('organizations.import.confirm.sectorLabel', 'Secteur')}
          value={
            report.sector_template_id ?? t('organizations.import.confirm.sectorNone', 'Aucun')
          }
        />
        <Row label={t('organizations.import.counts.domains', 'Domaines')} value={String(report.counts.domains)} />
        <Row label={t('organizations.import.counts.usecases', "Cas d'usage")} value={String(report.counts.usecases)} />
        <Row label={t('organizations.import.counts.cadrages', 'Cadrages')} value={String(report.counts.cadrages)} />
      </dl>

      <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <span>
          {t(
            'organizations.import.confirm.warning',
            'Une seule version active par cas d\'usage est autorisée : si plusieurs cadrages sont définis, seul le premier sera marqué comme actif.',
          )}
        </span>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="px-4 py-2 flex items-center justify-between gap-3">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="font-medium text-right truncate max-w-[60%]" title={value}>
      {value}
    </dd>
  </div>
);

export default StepConfirm;
